import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  EVENTO_SESION_EXPIRADA,
  cambiarPassword as cambiarPasswordApi,
  cerrarSesionServidor,
  login as loginApi,
  sesionActual,
} from "@/lib/api";
import type { Rol } from "@/types";

interface SesionUsuario {
  sub: string;
  rol: Rol;
  /** Tenant del usuario (null = global: admin/sistema ven todas). */
  empresa: string | null;
}

interface AuthContextValue {
  sesion: SesionUsuario | null;
  cargando: boolean;
  /** Autentica con credenciales. El rol lo decide el servidor. */
  iniciar: (username: string, password: string) => Promise<void>;
  /** True mientras el usuario no rote la contraseña provisional. */
  debeCambiarPassword: boolean;
  cambiarPassword: (actual: string, nueva: string) => Promise<void>;
  cerrar: () => void;
  // Helpers de capacidad por rol (F3).
  // Jerarquía estricta: administrador ⊇ supervisor ⊇ analista.
  puedeAnalizar: () => boolean;     // bandeja, chat, aportar, override, reprocesar
  puedeAprobar: () => boolean;       // aprobar / rechazar / descargar definitiva
  puedeNotificar: () => boolean;     // notificar al cliente (= puedeAprobar)
  puedeAdministrar: () => boolean;   // monitor worker, gestión usuarios, catálogo
  esAdministrador: () => boolean;    // strictly administrador
  esSupervisor: () => boolean;       // strictly supervisor o administrador
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sesion, setSesion] = useState<SesionUsuario | null>(null);
  // Empieza en `true`: hasta preguntar a /auth/me no sabemos si hay sesión.
  const [cargando, setCargando] = useState(true);
  const [debeCambiar, setDebeCambiar] = useState(false);

  // Al montar, restablece la sesión desde la cookie HttpOnly preguntando
  // al servidor (el JWT ya no es legible por JS). También reacciona a los
  // 401 que emite el cliente axios cuando la sesión vence.
  useEffect(() => {
    let vivo = true;
    sesionActual()
      .then((u) => vivo && setSesion({ sub: u.sub, rol: u.rol as Rol, empresa: u.empresa }))
      .catch(() => vivo && setSesion(null))
      .finally(() => vivo && setCargando(false));

    const alExpirar = () => setSesion(null);
    window.addEventListener(EVENTO_SESION_EXPIRADA, alExpirar);
    return () => {
      vivo = false;
      window.removeEventListener(EVENTO_SESION_EXPIRADA, alExpirar);
    };
  }, []);

  const iniciar = useCallback(async (username: string, password: string) => {
    setCargando(true);
    try {
      const resp = await loginApi(username, password);
      // El JWT viaja en la cookie; del body tomamos identidad y rol. La
      // empresa (para el filtro multi-tenant del SPA) la traemos de /auth/me.
      setDebeCambiar(Boolean(resp.debe_cambiar_password));
      let empresa: string | null = null;
      try {
        empresa = (await sesionActual()).empresa;
      } catch {
        empresa = null;
      }
      setSesion({ sub: resp.sub, rol: resp.rol, empresa });
    } finally {
      setCargando(false);
    }
  }, []);

  const cambiarPassword = useCallback(async (actual: string, nueva: string) => {
    await cambiarPasswordApi(actual, nueva);
    setDebeCambiar(false);
  }, []);

  const cerrar = useCallback(() => {
    void cerrarSesionServidor();
    setDebeCambiar(false);
    setSesion(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const rol = sesion?.rol;
    const esAdmin = rol === "administrador";
    const esSup = esAdmin || rol === "supervisor";
    const esAnal = esSup || rol === "analista";
    return {
      sesion,
      cargando,
      iniciar,
      debeCambiarPassword: debeCambiar,
      cambiarPassword,
      cerrar,
      puedeAnalizar: () => esAnal,
      puedeAprobar: () => esSup,
      puedeNotificar: () => esSup,
      puedeAdministrar: () => esAdmin,
      esAdministrador: () => esAdmin,
      esSupervisor: () => esSup,
    };
  }, [sesion, cargando, iniciar, debeCambiar, cambiarPassword, cerrar]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
